import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  Archive,
  ArchiveRestore,
  Download,
  HardDriveUpload,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/efs/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { listCompanies } from '@/features/settings/companies/api';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { companyFileContentUrl } from './api';
import {
  CompanyFilePreviewDialog,
  CompanyFileThumbnail,
  PreviewButton,
} from './CompanyFilePreview';
import { bytesToGiB, bytesToHuman, isUsageOver, isUsageWarning, usagePercent } from './format';
import { useFileLibraryFeatureEnabled } from './useFileLibraryFeature';
import {
  useArchiveCompanyFile,
  useCompanyFileReferences,
  useFileLibraryFiles,
  useFileLibraryUsage,
  useRestoreCompanyFile,
  useUploadCompanyFile,
} from './useFileLibrary';
import type { CompanyFileDto, CompanyFileMimeGroup, FileLibraryListParams } from './types';

const NS = 'fileLibrary';

const mimeGroups: CompanyFileMimeGroup[] = [
  'all',
  'image',
  'video',
  'pdf',
  'spreadsheet',
  'document',
  'other',
];
const statuses = ['ready', 'archived', 'all'] as const;

const canChooseCompany = (user: ReturnType<typeof useCurrentUser>): boolean =>
  hasPermission(user, 'system.admin') || user?.partyType === 'platform';

const UsageCard = ({ usage }: { usage: ReturnType<typeof useFileLibraryUsage>['data'] }) => {
  const { t } = useTranslation(NS);
  const percent = usagePercent(usage);
  const over = isUsageOver(usage);
  const warning = isUsageWarning(usage);
  const barClass = over ? 'bg-efs-error' : warning ? 'bg-amber-500' : 'bg-efs-accent';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>{t('efs.file_library.usage.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-4">
          <div>
            <div className="text-xs text-efs-text-muted">{t('efs.file_library.usage.used')}</div>
            <div className="text-lg font-semibold">{bytesToGiB(usage?.used_bytes ?? 0)}</div>
          </div>
          <div>
            <div className="text-xs text-efs-text-muted">
              {t('efs.file_library.usage.included')}
            </div>
            <div className="text-lg font-semibold">
              {bytesToGiB(usage?.included_bytes ?? 10_737_418_240)}
            </div>
          </div>
          <div>
            <div className="text-xs text-efs-text-muted">{t('efs.file_library.usage.overage')}</div>
            <div
              className={over ? 'text-lg font-semibold text-efs-error' : 'text-lg font-semibold'}
            >
              {bytesToHuman(usage?.overage_bytes ?? 0)}
            </div>
          </div>
          <div>
            <div className="text-xs text-efs-text-muted">
              {t('efs.file_library.usage.reserved')}
            </div>
            <div className="text-lg font-semibold">{bytesToHuman(usage?.reserved_bytes ?? 0)}</div>
          </div>
        </div>
        <div>
          <div className="h-2 overflow-hidden rounded-full bg-efs-bg-subtle">
            <div className={barClass} style={{ width: `${percent}%`, height: '100%' }} />
          </div>
          <div className="mt-1 text-xs text-efs-text-muted">
            {t('efs.file_library.usage.warning_line', {
              threshold: bytesToGiB((usage?.effective_limit_bytes ?? 10_737_418_240) * 0.8),
            })}
          </div>
        </div>
        {warning ? (
          <Alert variant={over ? 'destructive' : 'default'}>
            <AlertTitle>
              {over
                ? t('efs.file_library.usage.over_title')
                : t('efs.file_library.usage.warn_title')}
            </AlertTitle>
            <AlertDescription>
              {over
                ? t('efs.file_library.usage.over_message')
                : t('efs.file_library.usage.warn_message')}
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
};

const UploadCard = ({ companyId, canUpload }: { companyId?: string; canUpload: boolean }) => {
  const { t } = useTranslation(NS);
  const upload = useUploadCompanyFile();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] ?? null);
    setError(null);
  };

  const onUpload = async () => {
    if (!file) {
      setError(t('efs.file_library.validation.file_required'));
      return;
    }
    try {
      await upload.mutateAsync({ file, company_id: companyId });
      setFile(null);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('efs.file_library.upload.failed'));
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>{t('efs.file_library.upload.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input type="file" onChange={onFile} disabled={!canUpload || upload.isPending} />
        {file ? (
          <div className="text-xs text-efs-text-muted">
            {file.name} · {bytesToHuman(file.size)}
          </div>
        ) : null}
        {error ? <div className="text-sm text-efs-error">{error}</div> : null}
        <Button onClick={onUpload} disabled={!canUpload || !file || upload.isPending}>
          <HardDriveUpload className="h-4 w-4" />
          {upload.isPending
            ? t('efs.file_library.upload.uploading')
            : t('efs.file_library.upload.action')}
        </Button>
      </CardContent>
    </Card>
  );
};

const ReferencePanel = ({ file }: { file: CompanyFileDto | null }) => {
  const { t } = useTranslation(NS);
  const refs = useCompanyFileReferences(file?.company_file_id);
  if (!file) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>{t('efs.file_library.references.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {refs.isLoading ? (
          <div className="text-sm text-efs-text-muted">{t('efs.file_library.loading')}</div>
        ) : (refs.data?.references ?? []).length === 0 ? (
          <div className="text-sm text-efs-text-muted">
            {t('efs.file_library.references.empty')}
          </div>
        ) : (
          <ul className="space-y-2">
            {(refs.data?.references ?? []).map((ref) => (
              <li
                key={ref.reference_id}
                className="rounded-md border border-efs-border p-3 text-sm"
              >
                <div className="font-medium text-efs-text-primary">{ref.label}</div>
                <div className="mt-1 text-xs text-efs-text-muted">{ref.reference_type}</div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export const FileLibraryPage = () => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();
  const enabled = useFileLibraryFeatureEnabled();
  const [q, setQ] = useState('');
  const [mimeGroup, setMimeGroup] = useState<CompanyFileMimeGroup>('all');
  const [status, setStatus] = useState<(typeof statuses)[number]>('ready');
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<CompanyFileDto | null>(null);
  const [previewFile, setPreviewFile] = useState<CompanyFileDto | null>(null);
  const companySelectable = canChooseCompany(user);

  const companies = useQuery({
    queryKey: ['companies', 'file-library-processors'],
    queryFn: () => listCompanies({ party_type: 'processor', active_only: true, per_page: 200 }),
    enabled: enabled && companySelectable,
  });

  useEffect(() => {
    if (companySelectable && !companyId && companies.data?.[0]?.company_id) {
      setCompanyId(companies.data[0].company_id);
    }
  }, [companies.data, companyId, companySelectable]);

  useEffect(() => {
    setCursor(undefined);
    setSelected(null);
    setPreviewFile(null);
  }, [companyId, mimeGroup, q, status]);

  const companyScopeReady = !companySelectable || Boolean(companyId);

  const usage = useFileLibraryUsage(companyId, enabled && companyScopeReady);
  const files = useFileLibraryFiles(
    useMemo((): FileLibraryListParams => {
      const filters = {
        q,
        mime_group: mimeGroup,
        status,
        cursor,
      };
      return companyId ? { ...filters, company_id: companyId } : filters;
    }, [companyId, cursor, mimeGroup, q, status]),
    enabled && companyScopeReady,
  );
  const archiveFile = useArchiveCompanyFile();
  const restoreFile = useRestoreCompanyFile();

  const canUpload =
    hasPermission(user, 'file_library.upload') || hasPermission(user, 'file_library.manage');
  const canArchive =
    hasPermission(user, 'file_library.archive') || hasPermission(user, 'file_library.manage');

  if (!enabled) {
    return (
      <div className="space-y-4 p-6">
        <PageHeader title={t('efs.file_library.title')} subtitle={t('efs.file_library.subtitle')} />
        <Alert>
          <AlertTitle>{t('efs.file_library.feature_disabled_title')}</AlertTitle>
          <AlertDescription>{t('efs.file_library.feature_disabled')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        title={t('efs.file_library.title')}
        subtitle={t('efs.file_library.subtitle')}
        count={files.data?.files.length}
        actions={
          companySelectable ? (
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder={t('efs.file_library.company.own')} />
              </SelectTrigger>
              <SelectContent>
                {(companies.data ?? []).map((company) => (
                  <SelectItem key={company.company_id} value={company.company_id}>
                    {company.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null
        }
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-5">
          <UsageCard usage={usage.data} />
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>{t('efs.file_library.files.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2 md:grid-cols-[1fr_12rem_12rem_auto]">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-efs-text-muted" />
                  <Input
                    value={q}
                    onChange={(event) => setQ(event.target.value)}
                    className="pl-9"
                    placeholder={t('efs.file_library.filters.search_placeholder')}
                  />
                </div>
                <Select
                  value={mimeGroup}
                  onValueChange={(value) => setMimeGroup(value as CompanyFileMimeGroup)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {mimeGroups.map((group) => (
                      <SelectItem key={group} value={group}>
                        {t(`efs.file_library.mime.${group}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={status}
                  onValueChange={(value) => setStatus(value as (typeof statuses)[number])}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => (
                      <SelectItem key={s} value={s}>
                        {t(`efs.file_library.status.${s}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => void files.refetch()}>
                  <RefreshCw className="h-4 w-4" />
                  {t('efs.file_library.actions.refresh')}
                </Button>
              </div>

              <div className="overflow-hidden rounded-md border border-efs-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('efs.file_library.files.name')}</TableHead>
                      <TableHead>{t('efs.file_library.files.size')}</TableHead>
                      <TableHead>{t('efs.file_library.files.status')}</TableHead>
                      <TableHead>{t('efs.file_library.files.refs')}</TableHead>
                      <TableHead className="text-right">
                        {t('efs.file_library.files.actions')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {files.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={5}>{t('efs.file_library.loading')}</TableCell>
                      </TableRow>
                    ) : (files.data?.files ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>{t('efs.file_library.empty')}</TableCell>
                      </TableRow>
                    ) : (
                      (files.data?.files ?? []).map((file) => (
                        <TableRow
                          key={file.company_file_id}
                          className="cursor-pointer"
                          onClick={() => setSelected(file)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <CompanyFileThumbnail
                                file={file}
                                className="h-12 w-12"
                                onPreview={() => setPreviewFile(file)}
                              />
                              <div className="min-w-0">
                                <div className="truncate font-medium">{file.display_name}</div>
                                <div className="truncate text-xs text-efs-text-muted">
                                  {file.detected_mime_type ??
                                    t('efs.file_library.preview.unknown_type')}
                                </div>
                                <div className="truncate font-mono text-xs text-efs-text-muted">
                                  {file.sha256}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{bytesToHuman(file.size_bytes)}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                file.lifecycle_status === 'archived' ? 'outline' : 'secondary'
                              }
                            >
                              {t(`efs.file_library.status.${file.lifecycle_status}`, {
                                defaultValue: file.lifecycle_status,
                              })}
                            </Badge>
                          </TableCell>
                          <TableCell>{file.reference_count ?? 0}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <PreviewButton
                                file={file}
                                compact
                                onPreview={() => setPreviewFile(file)}
                              />
                              <Button
                                asChild
                                variant="ghost"
                                size="icon"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <a
                                  href={companyFileContentUrl(file.company_file_id)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Download className="h-4 w-4" />
                                  <span className="sr-only">
                                    {t('efs.file_library.actions.download')}
                                  </span>
                                </a>
                              </Button>
                              {file.lifecycle_status === 'archived' ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={!canArchive || restoreFile.isPending}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    restoreFile.mutate(file.company_file_id);
                                  }}
                                >
                                  <ArchiveRestore className="h-4 w-4" />
                                  <span className="sr-only">
                                    {t('efs.file_library.actions.restore')}
                                  </span>
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={!canArchive || archiveFile.isPending}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    archiveFile.mutate(file.company_file_id);
                                  }}
                                >
                                  <Archive className="h-4 w-4" />
                                  <span className="sr-only">
                                    {t('efs.file_library.actions.archive')}
                                  </span>
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {files.data?.next_cursor ? (
                <Button
                  variant="outline"
                  onClick={() => setCursor(files.data?.next_cursor ?? undefined)}
                >
                  {t('efs.file_library.actions.next_page')}
                </Button>
              ) : null}
            </CardContent>
          </Card>
        </div>
        <div className="space-y-5">
          <UploadCard companyId={companyId} canUpload={canUpload && companyScopeReady} />
          <ReferencePanel file={selected} />
        </div>
      </div>
      <CompanyFilePreviewDialog
        file={previewFile}
        open={Boolean(previewFile)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPreviewFile(null);
        }}
      />
    </div>
  );
};

export default FileLibraryPage;
